import { useState } from "react";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { PrimaryButton } from "../Buttons";
import {
  PasswordInput,
  SelectInput,
  TelephoneInput,
  TextInput,
  NumberInput,
} from "../Inputs";
import {useAdminRegistrationStepStore, useLoaderStore, useRegistrationStore} from "../../context";
import { useAlert, useApi } from "../../hooks";
import constants from "../../utils/constants";
import { isValidEmail, isValidPassword } from "../../utils/helpers";
import { formatPhoneNumber } from "react-phone-number-input";
import {Tooltip, Tooltip as ReactTooltip} from 'react-tooltip'
import HSTooltip from "@preline/tooltip";
import {InfoTooltip} from "../Displays";
import {InfoSlideOver} from "../Menus";

const FOUR_DAYS = 5760;
const SEVEN_DAYS = 10080;

export default function SystemTenantRegistrationForm({ handleSubmitCallback }) {
  const { setRegistrationSysTenant, registrationUser, registrationAccount } =
    useRegistrationStore();
  const {completeStep} = useAdminRegistrationStepStore();
  const alert = useAlert();
  const [name, setName] = useState(
    registrationAccount?.name ? `${registrationAccount?.name}` : "",
  );
  const [adminEmail, setAdminEmail] = useState(
    registrationAccount?.email || "",
  );
  const [adminMobilePhone, setAdminMobilePhone] = useState(
    registrationAccount?.mobilePhone || "",
  );
  const [delinquentGracePeriodMinutes, setDelinquentGracePeriodMinutes] =
    useState(FOUR_DAYS);
  const [warnRenewalMinutesThreshold, setWarnRenewalMinutesThreshold] =
    useState(SEVEN_DAYS);
  const [
    warnCancellationMinutesThreshold,
    setWarnCancellationMinutesThreshold,
  ] = useState(SEVEN_DAYS);
  const [processRenewalMinutesThreshold, setProcessRenewalMinutesThreshold] =
    useState(FOUR_DAYS);
  // const [getTenantUsersUri, setGetTenantUsersUri] = useState("");
  const [clientCommunicationSignature, setClientCommunicationSignature] =
    useState("");
  const [infoBarOpen, setInfoBarOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('US');


  function isValid() {
    if (!name || !name.trim()) {
      alert("Invalid input", "Please enter a valid name", "warning");
      return false;
    }
    if (!adminEmail || !adminEmail.trim() || !isValidEmail(adminEmail)) {
      alert("Invalid input", "Please enter a valid email", "warning");
      return false;
    }
    if (!adminMobilePhone || !adminMobilePhone.trim()) {
      alert("Invalid input", "Please enter a valid phone number", "warning");
      return false;
    }
    if (!isPossiblePhoneNumber(adminMobilePhone.trim(), selectedCountry)) {
      alert("Invalid input", "Please enter a valid phone number", "warning");
      return false;
    }
    if (
      processRenewalMinutesThreshold < 0 ||
      processRenewalMinutesThreshold > 43200
    ) {
      alert(
        "Invalid input",
        "Renewal hours must be between 0 and 43200 (1 month)",
        "warning",
      );
      return false;
    }
    return true;
  }

  const handleSave = async () => {
      if (!isValid()) return;
      let sysTenantInfo = {
        name: name,
        adminEmail: adminEmail,
        adminMobilePhone: adminMobilePhone,
        delinquentGracePeriodMinutes: delinquentGracePeriodMinutes,
        warnRenewalMinutesThreshold: warnRenewalMinutesThreshold,
        warnCancellationMinutesThreshold: warnCancellationMinutesThreshold,
        processRenewalMinutesThreshold: processRenewalMinutesThreshold,
        getTenantUsersUri: "MargotOkapiPlus Member",
        tenantUserCommunicationSentUri: null,
        clientCommunicationSignature: clientCommunicationSignature,
      };
      setRegistrationSysTenant(sysTenantInfo);
      completeStep();
  };

  const warnRenewalTooltip = new HSTooltip(document.querySelector('#warn-renewal-tooltip'));

  return (
    <div className="xs:w-10/12 md:w-3/4 space-y-6">
      <InfoSlideOver title={'test'} open={infoBarOpen}
                     onClose={() => setInfoBarOpen(false)} >
        <div>Hello</div>

      </InfoSlideOver>
      <div className="text-primaryColor text-lg font-semibold flex flex-row justify-start border-b">
        Tenant Information
      </div>
      <div className="overflow-y-auto flex flex-col">
        <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-x-4 gap-y-4">
          <div>
            <TextInput
              label="Name"
              name="name"
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <div className="peer/email">
              <TextInput
                label="Admin Email"
                id={"email-tool-tip"}
                name="adminEmail"
                value={adminEmail}
                maxLength={150}
                onChange={(e) => setAdminEmail(e.target.value)}
                required
                // onFocus={() => {
                //   setInfoBarOpen(true);
                // }}
                // onBlur={(e) => {
                //   setInfoBarOpen(false);
                // }}
              />
              <InfoTooltip id={"#email-tool-tip"}>
                {constants.Explanation.emailNotification}
              </InfoTooltip>
            </div>
            {/*<div className="hidden peer-focus-within/email:block text-red-700 text-sm">*/}
            {/*  <p>*/}
            {/*  */}
            {/*    /!*{`This is an email address used only when a notification or other processing failure occurs for this account. Email will be used preferentially if it is provided.`}*!/*/}
            {/*  </p>*/}
            {/*</div>*/}
          </div>
        </div>

        <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-x-4 gap-y-4 mt-4">
          <div className="peer/phone">
            <TelephoneInput
              label="Admin Mobile Phone"
              name="adminMobilePhone"
              id={"mobile-phone-tooltip"}
              inputId={"mobile-phone-tooltip"}
              value={adminMobilePhone}
              onChange={(e) => setAdminMobilePhone(e.target.value)}
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry}
              required
            />
            <InfoTooltip id={"#mobile-phone-tooltip"}>
              {constants.Explanation.mobileNotification}
            </InfoTooltip>
          </div>
          <div>
            <TextInput
              label="Email Signature"
              name="clientCommunicationSignature"
              value={clientCommunicationSignature}
              maxLength={300}
              onChange={(e) => setClientCommunicationSignature(e.target.value)}
            />
          </div>
        </div>
      </div>
      {/* <div className="inline-block w-full">
        <NumberInput
          label="Delinquent Subscription Grace Period (Minutes)"
          name="delinquentGracePeriodMinutes"
          id={"grace-period-tooltip"}
          value={delinquentGracePeriodMinutes}
          onChange={(e) => {
            setDelinquentGracePeriodMinutes(e.target.valueAsNumber);
          }}
          min={0}
        />
        <InfoTooltip id={"#grace-period-tooltip"}>
          {constants.Explanation.grace}
        </InfoTooltip>
      </div>
      <div id="warn-renewal-tooltip" className="inline-block w-full">
        <NumberInput
          label="Renewal Warning Threshold (Minutes)"
          name="warningRenewalHours"
          id={"renewal-warning-tooltip"}
          value={warnRenewalMinutesThreshold}
          onChange={(e) => {
            setWarnRenewalMinutesThreshold(e.target.valueAsNumber);
          }}
          min={0}
        />
        <InfoTooltip id={"#renewal-warning-tooltip"} place={"top"} width={60}>
          {constants.Explanation.warnRenewal}
        </InfoTooltip>
      </div>
      <div className="peer/warnCancel">
        <NumberInput
          label="Cancellation Warning Threshold (Minutes)"
          name="warningCancellationHours"
          id={"cancel-warning-tooltip"}
          value={warnCancellationMinutesThreshold}
          onChange={(e) => {
            setWarnCancellationMinutesThreshold(e.target.valueAsNumber);
          }}
          min={0}
        />
        <InfoTooltip id={"#cancel-warning-tooltip"} place={"top"}>
          {constants.Explanation.warnCancel}
        </InfoTooltip>
      </div>
      <div className="peer/renew">
        <NumberInput
          label="Process Renewal Threshold (Minutes)"
          name="processRenewalMinutesThreshold"
          id={"process-renew-tooltip"}
          value={processRenewalMinutesThreshold}
          onChange={(e) => {
            setProcessRenewalMinutesThreshold(e.target.valueAsNumber);
          }}
          min={0}
          required
        />
        <InfoTooltip id={"#process-renew-tooltip"} place={"top"} width={50}>
          {constants.Explanation.renew}
        </InfoTooltip>
      </div> */}
      <PrimaryButton text="NEXT: Privacy Policy and Terms and Conditions" onClick={handleSave} />
    </div>
  );
}
